import React, { useEffect, useState } from 'react';

import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import trashTokens from '@amzn/meridian-tokens/base/icon/trash';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import { type TableSortDirection } from '@amzn/meridian/table/table';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import {
  setIsTaskGroupModalOpen,
  setSelectedTaskGroupTableRow,
  setTaskGroupTableSettings,
  type TaskGroupTableDataRow,
} from 'src/store/adminConfigPageSlice/taskConfigPageSlice/taskConfigPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { filterSortAndPaginateTableData } from 'src/utils/tableUtils';

// **** Types **** //

interface SortProps {
  sortColumn: keyof TaskGroupTableDataRow;
  sortDirection: TableSortDirection;
}

interface TaskGroupTableProps {
  onDeleteClick: (taskGroup: TaskGroupTableDataRow) => void;
}

// **** Components **** //

/**
 * Default component
 */
const TaskGroupTable = ({ onDeleteClick }: TaskGroupTableProps) => {
  const dispatch = useAppDispatch();
  const [sortColumn, setSortColumn] = useState<keyof TaskGroupTableDataRow>('label');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('ascending');
  const { taskGroupTableData, taskGroupSearchFieldValue, taskGroupTableSettings } = useAppSelector(
    (state) => state.taskConfigPage,
  );
  const [sortedTableData, setSortedTableData] = useState(taskGroupTableData);
  const { currentPage, perPage, totalPages } = taskGroupTableSettings;

  // Filter, sorting, and pagination
  useEffect(() => {
    const data = filterSortAndPaginateTableData(
      taskGroupTableData,
      taskGroupSearchFieldValue,
      sortColumn,
      sortDirection,
      currentPage,
      ['id'],
      perPage,
    );
    setSortedTableData(data.paginatedData);
    dispatch(setTaskGroupTableSettings({ totalPages: data.totalPages }));
  }, [sortColumn, sortDirection, taskGroupTableData, taskGroupSearchFieldValue, currentPage, perPage]);

  // Go back to first page when filter/sort
  useEffect(() => {
    dispatch(setTaskGroupTableSettings({ currentPage: 1 }));
  }, [taskGroupSearchFieldValue, sortColumn, sortDirection]);

  // Set the currentPage to the totalPage if the currentPage ends up being
  // greater then the totalPage (this can happen when deleting groups).
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      dispatch(setTaskGroupTableSettings({ currentPage: totalPages }));
    }
  }, [currentPage, totalPages]);

  return (
    <Box overflowY="scroll" overflowX="scroll" type="outline" className="view-and-edit-tasks__table">
      <Table
        headerRows={1}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={(props: SortProps) => {
          setSortDirection(props.sortDirection);
          setSortColumn(props.sortColumn);
        }}
        showDividers={true}
        fixHeaderRows={true}
        rowComponents={[UncontrolledExpandableRow, TableRow]}
      >
        {/* Header */}
        <TableRow>
          <TableCell width="20%" sortColumn="label">
            Group Name
          </TableCell>
          <TableCell sortColumn="region">State/Region/Province</TableCell>
          <TableCell sortColumn="country">Country</TableCell>
          <TableCell sortColumn="identifyBodyLocation">Identify Body Location?</TableCell>
          <TableCell sortColumn="isMeasuredBy">Is Measured By?</TableCell>
          <TableCell sortColumn="description">Description</TableCell>
          <TableCell>Edit</TableCell>
          <TableCell>Delete</TableCell>
        </TableRow>

        {/* Rows */}
        {sortedTableData.map((taskGroup) => (
          <UncontrolledExpandableRow key={taskGroup.id} highlightOnHover={true} data-testid="task-group-table__row">
            <TableCell>{taskGroup.label}</TableCell>
            <TableCell>{taskGroup.region}</TableCell>
            <TableCell>{taskGroup.country}</TableCell>
            <TableCell>{taskGroup.identifyBodyLocation ? 'Yes' : 'No'}</TableCell>
            <TableCell>{taskGroup.isMeasuredBy}</TableCell>
            <TableCell>{''}</TableCell>
            <TableCell>
              <Button
                type="icon"
                aria-label={`Edit ${taskGroup.label}`}
                className="edit-and-delete-task-groups-icon"
                data-testid="task-group-table__row__open-edit-modal-button"
                propagateClickEvent={false}
                onClick={() => {
                  dispatch(setIsTaskGroupModalOpen(true));
                  dispatch(setSelectedTaskGroupTableRow(taskGroup));
                }}
              >
                <Icon tokens={editTokens}></Icon>
              </Button>
            </TableCell>
            <TableCell>
              <Button
                type="icon"
                aria-label={`Delete ${taskGroup.label}`}
                className="edit-and-delete-task-groups-icon"
                data-testid="task-group-table__row__delete-button"
                propagateClickEvent={false}
                onClick={() => onDeleteClick(taskGroup)}
              >
                <Icon tokens={trashTokens} />
              </Button>
            </TableCell>
            {taskGroup.tasks?.map((task) => (
              <TableRow key={taskGroup.id + '-' + task.id} backgroundColor="secondary">
                <TableCell>{task.label}</TableCell>
                <TableCell>{taskGroup.region}</TableCell>
                <TableCell>{taskGroup.country}</TableCell>
                <TableCell>{task.identifyBodyLocation ? 'Yes' : 'No'}</TableCell>
                <TableCell>{task.isMeasuredBy}</TableCell>
                <TableCell>{''}</TableCell>
                <TableCell>{''}</TableCell>
                <TableCell>{''}</TableCell>
              </TableRow>
            ))}
          </UncontrolledExpandableRow>
        ))}
      </Table>
    </Box>
  );
};

export default TaskGroupTable;
